import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw, Router } from 'vue-router';
import type { App } from 'vue';
import modules from './modules'
import { NO_RESET_WHITE_LIST } from '@/constants';
import globalData from '@/globalData';
import { ROUTER_URL } from '@/constants/env';
import { useUserStoreWithOut } from '@/store/modules/user';

const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/views/landing/index.vue'),
    name: 'Landing',
    meta: {
      noLogin: true,
      hidden: true,
      noTagsView: true,
    },
  },
  {
    path: '/redirect',
    name: 'RedirectParent',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {},
      },
    ],
    meta: {
      hidden: true,
      noTagsView: true,
    },
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      noLogin: true,
      hidden: true,
      title: '登录',
      noTagsView: true,
    },
  },
  {
    path: '/light/mobile-upload/:token',
    component: () => import('@/light-page/mobile-upload/index.vue'),
    name: 'LightMobileUpload',
    meta: {
      noLogin: true,
      hidden: true,
      title: '图片上传',
      noTagsView: true,
    },
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true,
    },
  },
  {
    path: '/401',
    component: () => import('@/views/Error/401.vue'),
    name: 'NoUser',
    meta: {
      noLogin: true,
      hidden: true,
      title: '401',
      noTagsView: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/404',
    meta: {
      hidden: true,
    },
  },
];
let router: Router = createRouter({
  history: createWebHistory(ROUTER_URL),
  strict: true,
  routes: [...constantRouterMap as RouteRecordRaw[]],
  scrollBehavior: () => ({ left: 0, top: 0 }),
});

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route;
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name);
    }
  });
};

export const setupRouter = (app: App<Element>) => {
  router.beforeEach(async (to, _, next) => {
    if (to.meta.noLogin) {
      next();
      return;
    }

    const token = globalData.getTokenInfo();
    if (!token) {
      next(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
      return;
    }

    // 检查权限：如果路由需要特定权限，则验证用户是否有该权限
    const { useAppStore } = await import('@/store/modules/app');
    const { useUserStore } = await import('@/store/modules/user');
    const userStore = useUserStore();
    const appStore = useAppStore();

    // 如果权限还未加载，等待权限加载完成
    if (!userStore.getIsPermissionLoaded) {
      appStore.setPageLoading(true);
      
      if (!userStore.userInfo?.permissions) {
        await userStore.refreshUserInfo();
      }
      
      appStore.setPageLoading(false);
    }

    // 检查菜单权限或按钮权限
    if (to.meta.requiresPermission && to.name) {
      const routeName = to.name as string;
      
      // 获取权限状态
      const menuPermissions = userStore.permissions;
      const buttonPermissions = userStore.buttonPermissions;
      
      // 如果两个权限列表都为空，说明权限体系未配置，允许访问
      if (menuPermissions.length === 0 && buttonPermissions.length === 0) {
        next();
        return;
      }
      
      // 如果配置了权限，检查用户是否拥有该路由的权限
      // 只要菜单权限或按钮权限其中之一包含该路由名，就允许访问
      const hasMenuPermission = menuPermissions.includes(routeName);
      const hasButtonPermission = buttonPermissions.includes(routeName);
      
      if (!hasMenuPermission && !hasButtonPermission) {
        next('/401');
        return;
      }
    }

    next();
  });

  router.afterEach((to) => {
    const userStore = useUserStoreWithOut();
    const pageTitle = (to.meta?.title as string) || '';
    const orgName = userStore.userInfo?.organizationName || '';
    document.title = orgName ? `${pageTitle} - ${orgName}` : pageTitle || '健康扫描';
  });

  modules.forEach(el => {
    router.addRoute(el as RouteRecordRaw)
  });
  app.use(router);
};

export default router;
